import { deferredImports } from '../utils/tbtOptimization';
import { logger } from '../utils/logger';
import { libraryCacheManager } from '../utils/libraryCache';

export const getBackgroundColor = async (element: HTMLElement): Promise<string> => {
  return new Promise((resolve) => {
    try {
      // Get computed styles
      const computedStyle = window.getComputedStyle(element);
      const bgColor = computedStyle.backgroundColor;

      // If background color is transparent or not set, use optimized ColorThief
      if (bgColor === 'transparent' || bgColor === 'rgba(0, 0, 0, 0)') {
        // Create a temporary canvas
        const canvas = document.createElement('canvas');
        canvas.width = element.offsetWidth || 300;
        canvas.height = element.offsetHeight || 200;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve('rgb(0, 65, 122)');
          return;
        }

        // Draw element background
        ctx.fillStyle =
          computedStyle.backgroundImage !== 'none'
            ? computedStyle.backgroundImage
            : 'rgb(255, 255, 255)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Convert canvas to image
        const img = new Image();
        img.src = canvas.toDataURL();

        img.onload = async () => {
          try {
            // Generate cache key based on element dimensions and background
            const cacheKey = `bg_${element.offsetWidth}_${element.offsetHeight}_${computedStyle.backgroundImage}`;

            // Check cache first
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
              resolve(cached);
              return;
            }

            // Use cache-first for ColorThief, fallback to deferred import
            const cachedModule = libraryCacheManager.getCachedLibrary('colorthief') as
              | typeof import('colorthief')
              | null;
            const module =
              cachedModule ?? ((await deferredImports.colorThief()) as typeof import('colorthief'));
            const colorThief = new module.default();

            const colorResult = colorThief.getColor(img);
            if (colorResult && Array.isArray(colorResult) && colorResult.length >= 3) {
              const [r, g, b] = colorResult;
              const color = `rgb(${r}, ${g}, ${b})`;

              // Cache the result
              sessionStorage.setItem(cacheKey, color);
              resolve(color);
            } else {
              logger.warn('ColorThief returned invalid color data:', colorResult);
              resolve('rgb(0, 65, 122)');
            }
          } catch (error) {
            logger.warn('Error extracting color:', error);
            resolve('rgb(0, 65, 122)');
          }
        };

        img.onerror = () => {
          logger.warn('Error loading image from canvas');
          resolve('rgb(0, 65, 122)');
        };
      } else {
        // Return the computed background color
        resolve(bgColor);
      }
    } catch (error) {
      logger.warn('Error getting background color:', error);
      resolve('rgb(0, 65, 122)');
    }
  });
};
